import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { useToast } from '@/storybook/components/Toast/Toast';
import type { ChatbotConversation } from '@/models/chatbot';
import useChatbotStore from '../store/useChatbotStore';
import { getHistoryErrorMessageKey } from './historyError';

interface UseDeleteConversationParams {
  remove: UseMutationResult<void, Error, string>;
}

const useDeleteConversation = ({ remove }: UseDeleteConversationParams) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const activeConversationId = useChatbotStore(s => s.activeConversationId);
  const clearConversation = useChatbotStore(s => s.clearConversation);

  const [deleteTarget, setDeleteTarget] = useState<ChatbotConversation | null>(null);

  const deleteConversationTitle = (deleteTarget?.title ?? '').trim() || t('components.aiChatDrawer.history.untitled');

  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) {
      return;
    }

    const { id } = deleteTarget;

    remove.mutate(id, {
      onSuccess: () => {
        if (id === activeConversationId) {
          clearConversation();
        }

        makeToast({ content: t('components.aiChatDrawer.history.deleteSuccess'), appearance: 'success' });
      },
      onError: error => {
        makeToast({ content: t(getHistoryErrorMessageKey('delete', error)), appearance: 'error' });
      },
      onSettled: () => setDeleteTarget(null),
    });
  }, [activeConversationId, clearConversation, deleteTarget, makeToast, remove, t]);

  return {
    deleteTarget,
    setDeleteTarget,
    deleteConversationTitle,
    handleDeleteCancel,
    handleDeleteConfirm,
    isDeletePending: remove.isPending,
  };
};

export default useDeleteConversation;
